import { createFileRoute } from "@tanstack/react-router"
import { useMutation } from "convex/react"
import { useState, type FormEvent } from "react"
import { PublicInfoPage } from "@/components/PublicInfoPage"
import { api } from "../../convex/_generated/api"

type SubmitStatus = "idle" | "submitting" | "success" | "error"
type WouldReturn = "" | "yes" | "maybe" | "no"

const MAX_SHOW_VENUE_LENGTH = 160
const MAX_CONTACT_LENGTH = 160

const fieldClass =
  "survey-field mt-2 w-full rounded-2xl border border-[#c8e840]/60 bg-[#f8f7ff] px-4 py-3 text-base font-semibold text-[#160a3d] shadow-[inset_0_0_0_1px_rgba(22,10,61,0.08)] outline-none transition focus:border-[#c8e840] focus:ring-4 focus:ring-[#c8e840]/25 [color-scheme:light]"

function cleanSearchString(value: unknown, maxLength: number) {
  return typeof value === "string" ? value.trim().slice(0, maxLength) : ""
}

export const Route = createFileRoute("/survey")({
  validateSearch: (search) => {
    const show = cleanSearchString(search.show, MAX_SHOW_VENUE_LENGTH)
    const contact = cleanSearchString(search.contact, MAX_CONTACT_LENGTH)
    return {
      ...(show ? { show } : {}),
      ...(contact ? { contact } : {}),
    }
  },
  component: SurveyPage,
})

function SurveyPage() {
  const search = Route.useSearch()
  const submitSurvey = useMutation(api.surveys.submitPostGame)
  const [showVenue, setShowVenue] = useState(search.show ?? "")
  const [feedback, setFeedback] = useState("")
  const [wouldReturn, setWouldReturn] = useState<WouldReturn>("")
  const [contact, setContact] = useState(search.contact ?? "")
  const [status, setStatus] = useState<SubmitStatus>("idle")
  const [error, setError] = useState("")

  async function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault()
    const cleanFeedback = feedback.trim()
    if (!cleanFeedback) {
      setStatus("error")
      setError("Give us at least one host note. Brutal is fine. Blank is not.")
      return
    }

    setStatus("submitting")
    setError("")
    try {
      await submitSurvey({
        showVenue: showVenue.trim() || undefined,
        feedback: cleanFeedback,
        wouldReturn: wouldReturn || undefined,
        contact: contact.trim() || undefined,
      })
      setStatus("success")
    } catch (err) {
      setStatus("error")
      setError(err instanceof Error ? err.message : "Could not send that yet.")
    }
  }

  const submitting = status === "submitting"

  return (
    <PublicInfoPage eyebrow="Host debrief" title="Help us tighten the next show.">
      <p>
        You ran the room. Tell us what made hosting easy, what slowed you down,
        and what needs fixing before the next crowd starts yelling song titles
        with dangerous confidence.
      </p>

      <form
        autoComplete="off"
        className="not-prose mt-8 space-y-5"
        onSubmit={handleSubmit}
      >
        <label className="block">
          <span className="text-sm font-extrabold uppercase tracking-[0.16em] text-[#c8e840]">
            Show / venue
          </span>
          <input
            autoComplete="off"
            value={showVenue}
            onChange={(event) => setShowVenue(event.target.value)}
            maxLength={160}
            name="wg-show-context"
            placeholder="Venue, date, or show nickname"
            className={fieldClass}
          />
        </label>

        <label className="block">
          <span className="text-sm font-extrabold uppercase tracking-[0.16em] text-[#c8e840]">
            Host notes
          </span>
          <textarea
            autoComplete="off"
            value={feedback}
            onChange={(event) => setFeedback(event.target.value)}
            maxLength={2000}
            name="wg-host-notes"
            required
            rows={7}
            placeholder="What helped? What dragged? Where did the host flow get weird?"
            className={`${fieldClass} min-h-44 resize-y leading-7`}
          />
        </label>

        <fieldset className="space-y-3">
          <legend className="text-sm font-extrabold uppercase tracking-[0.16em] text-[#c8e840]">
            Would you host again?
          </legend>
          <div className="grid gap-3 sm:grid-cols-3">
            {[
              ["yes", "Book it"],
              ["maybe", "After tweaks"],
              ["no", "Not yet"],
            ].map(([value, label]) => {
              const selected = wouldReturn === value
              return (
                <button
                  key={value}
                  type="button"
                  onClick={() => setWouldReturn(value as WouldReturn)}
                  className={[
                    "min-h-12 rounded-full border px-4 py-3 text-sm font-extrabold transition",
                    selected
                      ? "border-[#c8e840] bg-[#c8e840] text-[#160a3d]"
                      : "border-white/14 bg-white/[0.06] text-white hover:border-[#c8e840]/70",
                  ].join(" ")}
                >
                  {label}
                </button>
              )
            })}
          </div>
        </fieldset>

        <label className="block">
          <span className="text-sm font-extrabold uppercase tracking-[0.16em] text-[#c8e840]">
            Best follow-up
          </span>
          <input
            autoComplete="off"
            value={contact}
            onChange={(event) => setContact(event.target.value)}
            maxLength={160}
            name="wg-host-followup"
            placeholder="Optional follow-up"
            className={fieldClass}
          />
        </label>

        <button
          type="submit"
          disabled={submitting}
          className="min-h-14 w-full rounded-full bg-[#c8e840] px-6 py-4 text-base font-black text-[#160a3d] transition hover:bg-[#d8ff39] disabled:cursor-wait disabled:opacity-70"
        >
          {submitting ? "Sending..." : "Send host notes"}
        </button>

        {status === "success" ? (
          <p className="rounded-2xl border border-[#c8e840]/35 bg-[#c8e840]/10 px-4 py-3 text-sm font-bold text-[#e9ff91]">
            Got it. We will tighten the host flow before the next show.
          </p>
        ) : null}

        {status === "error" && error ? (
          <p className="rounded-2xl border border-[#ff7ab8]/35 bg-[#ff7ab8]/10 px-4 py-3 text-sm font-bold text-[#ffc1dc]">
            {error}
          </p>
        ) : null}
      </form>
    </PublicInfoPage>
  )
}
